<template>
  <v-container class="mx-3 my-1" fluid>
    <v-row class="mx-7 justify-center mb-6">
      <div class="d-flex flex-column justify-center align-center">
        <v-col class="d-flex justify-center text-h3 font-weight-light text-center">
          {{ $t("instructions.billingAndRevenue") }}
        </v-col>
      </div>
    </v-row>

    <!-- TABS FOR BROKER -->
    <v-row v-if="isBroker" justify="center" class="mb-6">
      <v-col cols="auto">
        <v-tabs v-model="activeTab" background-color="transparent" color="primary" centered active-class="font-weight-bold">
          <v-tab>{{ $t('billing.tabs.revenueHistory') }}</v-tab>
          <v-tab>{{ $t('billing.tabs.partnerInvoices') }}</v-tab>
        </v-tabs>
      </v-col>
    </v-row>

    <!-- CONTENT FOR BROKER -->
    <v-tabs-items v-if="isBroker" v-model="activeTab" style="background: transparent;">
      <!-- Tab 1: Revenue History -->
      <v-tab-item style="background: transparent;">
        <!-- Top KPI cards: Consolidated Broker Overall -->
        <v-row class="ma-5 justify-center">
          <v-col cols="12" md="6" lg="5">
            <BillingKpiCard
              :label="$t('billing.kpi.currentMonthLabel')"
              :value="getRevenueValue(billingData.monthlyRevenueThisMonth, 'broker')"
              icon="mdi-calendar"
              avatarColor="primary"
              valueClass="success--text"
              :note="$t('billing.kpi.currentMonthNote')"
              noteIcon="mdi-clock-outline"
              noteIconColor="success"
            />
          </v-col>

          <v-col cols="12" md="6" lg="5">
            <BillingKpiCard
              :label="$t('billing.kpi.lastMonthLabel')"
              :value="getRevenueValue(billingData.monthlyRevenueLastMonth, 'broker')"
              icon="mdi-calendar-check"
              avatarColor="secondary"
              :note="$t('billing.broker.allPartnersNote')"
            />
          </v-col>
        </v-row>

        <!-- Consolidated Revenue Statement Table -->
        <v-row class="ma-5 justify-center">
          <v-col cols="12" lg="10">
            <v-card class="py-5 px-4 billing-table-card" outlined>
              <div class="d-flex justify-space-between align-center px-4 pb-4">
                <div>
                  <div class="d-flex align-center mb-1">
                    <h2 class="text-h5 font-weight-medium mr-3">{{ $t('billing.tabs.revenueHistory') }}</h2>
                    <span class="month-range-badge">{{ $t('billing.allPartnersEarningsBadge', { n: consolidatedMonthlyInvoices.length }) }}</span>
                  </div>
                  <span class="subtitle-2 grey--text">{{ $t('billing.statementTableSubtitleAll') }}</span>
                </div>
                <v-btn
                  small
                  icon
                  class="elevation-1"
                  color="primary"
                  :loading="loading"
                  @click="loadBillingData"
                >
                  <v-icon>mdi-sync</v-icon>
                </v-btn>
              </div>
              <RevenueHistoryTable
                :invoices="consolidatedMonthlyInvoices"
                :loading="loading"
                :brokerCnpj="getBrokerCnpj()"
              />
            </v-card>
          </v-col>
        </v-row>
      </v-tab-item>

      <!-- Tab 2: Partner Invoices -->
      <v-tab-item style="background: transparent;">
        <!-- Partner Autocomplete dropdown with disabled browser credentials autofill -->
        <v-row class="ma-5 justify-center">
          <v-col cols="12" lg="10">
            <v-autocomplete
              v-model="selectedPartnerCnpj"
              :items="filteredPartners"
              :label="$t('billing.broker.selectPartner')"
              :placeholder="$t('billing.broker.selectPartner')"
              clearable
              outlined
              dense
              hide-details
              item-value="cnpj"
              item-text="name"
              autocomplete="dont-autofill-partner"
              :filter="filterPartner"
              class="partner-selector"
              prepend-inner-icon="mdi-account-group-outline"
              :loading="loading"
              @change="onPartnerSelected"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title class="partner-name">{{ item.name }}</v-list-item-title>
                  <v-list-item-subtitle class="caption grey--text">{{ convertToCNPJ(item.cnpj) }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
              <template v-slot:selection="{ item }">
                <span class="partner-selection-text">{{ truncateName(item.name, 40) }}</span>
                <span class="caption grey--text ml-2">{{ convertToCNPJ(item.cnpj) }}</span>
              </template>
            </v-autocomplete>
          </v-col>
        </v-row>

        <!-- Placeholder when no partner selected -->
        <v-row v-if="!selectedPartnerCnpj" class="ma-5 justify-center">
          <v-col cols="12" lg="10">
            <v-card outlined class="pa-8 text-center grey--text subtitle-1" style="background: rgba(255, 255, 255, 0.01) !important; border: 1px dashed rgba(255, 255, 255, 0.15) !important; border-radius: 12px;">
              <v-icon large color="primary" class="mb-3">mdi-account-group-outline</v-icon>
              <div>{{ $t('billing.broker.selectPartnerPlaceholder') }}</div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Partner Specific KPIs and Table when selected -->
        <template v-else>
          <v-row class="ma-5 justify-center">
            <v-col cols="12" md="6" lg="5">
              <BillingKpiCard
                :label="$t('billing.kpi.currentMonthLabel')"
                :value="getRevenueValue(billingData.monthlyRevenueThisMonth, 'broker')"
                icon="mdi-calendar"
                avatarColor="primary"
                valueClass="success--text"
                :note="$t('billing.kpi.currentMonthNote')"
                noteIcon="mdi-clock-outline"
                noteIconColor="success"
              />
            </v-col>

            <v-col cols="12" md="6" lg="5">
              <BillingKpiCard
                :label="$t('billing.kpi.lastMonthLabel')"
                :value="getRevenueValue(billingData.monthlyRevenueLastMonth, 'broker')"
                icon="mdi-calendar-check"
                avatarColor="secondary"
              >
                <template v-slot:footer>
                  <div v-if="showLastMonthStatus" class="d-flex align-center">
                    <span class="subtitle-2 grey--text mr-2">{{ $t('billing.kpi.lastMonthInvoiceStatus') }}</span>
                    <v-chip
                      small
                      :color="getLastMonthStatusColor()"
                      text-color="white"
                      class="font-weight-black px-4"
                    >
                      {{ getLastMonthStatusText() }}
                    </v-chip>
                  </div>
                  <!-- Broker self earnings note -->
                  <div v-else-if="selectedPartnerCnpj === getBrokerCnpj()" class="d-flex align-center">
                    <v-icon small color="grey" class="mr-1">mdi-information-outline</v-icon>
                    <span class="subtitle-2 grey--text">{{ $t('billing.broker.ownEarningsNote') }}</span>
                  </div>
                  <div v-else class="d-flex align-center">
                    <v-icon small color="grey" class="mr-1">mdi-information-outline</v-icon>
                    <span class="subtitle-2 grey--text">{{ $t('billing.kpi.noLastMonthInvoice') }}</span>
                  </div>
                </template>
              </BillingKpiCard>
            </v-col>
          </v-row>

          <v-row class="ma-5 justify-center">
            <v-col cols="12" lg="10">
              <v-card class="py-5 px-4 billing-table-card" outlined>
                <div class="d-flex justify-space-between align-center px-4 pb-4">
                  <div>
                    <div class="d-flex align-center mb-1">
                      <h2 class="text-h5 font-weight-medium mr-3">{{ selectedPartnerCnpj === getBrokerCnpj() ? $t('billing.statementTitle') : $t('billing.table.title') }}</h2>
                      <span class="month-range-badge">{{ $t('billing.lastNMonths', { n: enrichedInvoices.length }) }}</span>
                    </div>
                    <span class="subtitle-2 grey--text">{{ selectedPartnerCnpj === getBrokerCnpj() ? $t('billing.statementSubtitle') : $t('billing.table.subtitle') }}</span>
                  </div>
                  <v-btn
                    small
                    icon
                    class="elevation-1"
                    color="primary"
                    :loading="loading"
                    @click="loadBillingData"
                  >
                    <v-icon>mdi-sync</v-icon>
                  </v-btn>
                </div>
                <InvoiceTable
                  :invoices="enrichedInvoices"
                  :loading="loading"
                  :show-actions="selectedPartnerCnpj !== getBrokerCnpj()"
                  :show-partner-column="false"
                  @toggle-paid="onTogglePaid"
                />
              </v-card>
            </v-col>
          </v-row>
        </template>
      </v-tab-item>
    </v-tabs-items>

    <!-- CONTENT FOR PARTNER (NON-BROKER) -->
    <template v-else>
      <v-row class="ma-5 justify-center">
        <v-col cols="12" md="6" lg="5">
          <BillingKpiCard
            :label="$t('billing.kpi.currentMonthLabel')"
            :value="getRevenueValue(billingData.monthlyRevenueThisMonth, 'partners')"
            icon="mdi-calendar"
            avatarColor="primary"
            valueClass="success--text"
            :note="$t('billing.kpi.currentMonthNote')"
            noteIcon="mdi-clock-outline"
            noteIconColor="success"
          />
        </v-col>

        <v-col cols="12" md="6" lg="5">
          <BillingKpiCard
            :label="$t('billing.kpi.lastMonthLabel')"
            :value="getRevenueValue(billingData.monthlyRevenueLastMonth, 'partners')"
            icon="mdi-calendar-check"
            avatarColor="secondary"
          >
            <template v-slot:footer>
              <div v-if="showLastMonthStatus" class="d-flex align-center">
                <span class="subtitle-2 grey--text mr-2">{{ $t('billing.kpi.lastMonthInvoiceStatus') }}</span>
                <v-chip
                  small
                  :color="getLastMonthStatusColor()"
                  text-color="white"
                  class="font-weight-black px-4"
                >
                  {{ getLastMonthStatusText() }}
                </v-chip>
              </div>
              <div v-else class="d-flex align-center">
                <v-icon small color="grey" class="mr-1">mdi-information-outline</v-icon>
                <span class="subtitle-2 grey--text">{{ $t('billing.kpi.noLastMonthInvoice') }}</span>
              </div>
            </template>
          </BillingKpiCard>
        </v-col>
      </v-row>

      <v-row class="ma-5 justify-center">
        <v-col cols="12" lg="10">
          <v-card class="py-5 px-4 billing-table-card" outlined>
            <div class="d-flex justify-space-between align-center px-4 pb-4">
              <div>
                <div class="d-flex align-center mb-1">
                  <h2 class="text-h5 font-weight-medium mr-3">{{ $t('billing.table.title') }}</h2>
                  <span class="month-range-badge">{{ $t('billing.lastNMonths', { n: enrichedInvoices.length }) }}</span>
                </div>
                <span class="subtitle-2 grey--text">{{ $t('billing.table.subtitle') }}</span>
              </div>
              <v-btn
                small
                icon
                class="elevation-1"
                color="primary"
                :loading="loading"
                @click="loadBillingData"
              >
                <v-icon>mdi-sync</v-icon>
              </v-btn>
            </div>
            <InvoiceTable
              :invoices="enrichedInvoices"
              :loading="loading"
              :show-actions="false"
              :show-partner-column="false"
              @toggle-paid="onTogglePaid"
            />
          </v-card>
        </v-col>
      </v-row>
    </template>

    <!-- Confirmation dialog -->
    <v-dialog v-model="confirmDialog" max-width="420" @keydown.esc="confirmDialog = false">
      <v-card class="confirm-dialog-card">
        <v-card-title class="text-h6 font-weight-semibold pb-2">
          {{ pendingItem && pendingItem.paid === 1 ? $t('billing.confirm.titleUnpaid') : $t('billing.confirm.titlePaid') }}
        </v-card-title>
        <v-card-text v-if="pendingItem" class="pt-0">
          <div class="confirm-detail-row mb-2">
            <span class="grey--text mr-2">{{ $t('billing.table.colMonth') }}:</span>
            <strong>{{ formatMonth(pendingItem.month) }}</strong>
          </div>
          <div class="confirm-detail-row mb-2" v-if="pendingItem.partner_name">
            <span class="grey--text mr-2">{{ $t('billing.table.colPartner') }}:</span>
            <strong style="max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: inline-block; vertical-align: bottom;" :title="pendingItem.partner_name">{{ pendingItem.partner_name }}</strong>
          </div>
          <div class="confirm-detail-row mb-2">
            <span class="grey--text mr-2">{{ $t('billing.table.colAmount') }}:</span>
            <strong>R$ {{ formatAmount(pendingItem.amount) }}</strong>
          </div>
          <div class="confirm-detail-row mt-3">
            <v-icon small :color="pendingItem.paid === 1 ? 'warning' : 'success'" class="mr-1">
              {{ pendingItem.paid === 1 ? 'mdi-close-circle-outline' : 'mdi-check-circle-outline' }}
            </v-icon>
            <span>{{ pendingItem.paid === 1 ? $t('billing.confirm.bodyUnpaid') : $t('billing.confirm.bodyPaid') }}</span>
          </div>
        </v-card-text>
        <v-card-actions class="px-6 pb-4">
          <v-spacer />
          <v-btn text @click="confirmDialog = false" :disabled="confirmingPaid">{{ $t('billing.confirm.cancel') }}</v-btn>
          <v-btn
            :color="pendingItem && pendingItem.paid === 1 ? 'warning' : 'success'"
            depressed
            :loading="confirmingPaid"
            @click="confirmTogglePaid"
          >
            {{ $t('billing.confirm.confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>


  </v-container>
</template>

<script>
import InvoiceTable from "@/components/billing/InvoiceTable.vue";
import RevenueHistoryTable from "@/components/billing/RevenueHistoryTable.vue";
import BillingKpiCard from "@/components/billing/BillingKpiCard.vue";
import { useBillingStore } from "@/store";
import { mapState, mapActions } from "pinia";
import convertToCNPJ, { formatDateYM, formatBRLPretty } from "@/utils";

export default {
  components: {
    InvoiceTable,
    RevenueHistoryTable,
    BillingKpiCard,
  },
  data() {
    return {
      activeTab: 0,
      selectedPartnerCnpj: null,
      confirmDialog: false,
      pendingItem: null,
      confirmingPaid: false,
    };
  },
  watch: {
    async activeTab(newTab) {
      if (newTab === 0) {
        this.selectedPartnerCnpj = null;
      }
      await this.loadBillingData();
    }
  },
  computed: {
    ...mapState(useBillingStore, ["billingData", "partners", "loading"]),

    isBroker() {
      return localStorage.getItem("userRole") === "broker";
    },

    /** Partners list for the selector — sourced from the aggregate response */
    partnerItems() {
      return this.partners || [];
    },

    filteredPartners() {
      const brokerCnpj = this.getBrokerCnpj();
      return this.partnerItems.filter(p => p.cnpj !== brokerCnpj);
    },

    showLastMonthStatus() {
      return this.lastMonthInvoice != null;
    },

    currentMonthStr() {
      const now = new Date();
      return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    },

    prevMonthStr() {
      const now = new Date();
      const prevMonthDt = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return `${prevMonthDt.getFullYear()}-${String(prevMonthDt.getMonth() + 1).padStart(2, '0')}`;
    },

    lastMonthInvoice() {
      return this.enrichedInvoices.find(inv => inv.month === this.prevMonthStr);
    },

    enrichedInvoices() {
      const invoices = this.billingData.invoices || [];
      return invoices.map(inv => {
        if (inv.month === this.currentMonthStr) {
          return { ...inv, status: "InProgress" };
        }
        let status = "Overdue";
        if (inv.paid === 1) {
          status = "Paid";
        } else if (inv.month >= this.prevMonthStr) {
          status = "Due";
        }
        return { ...inv, status };
      });
    },

    consolidatedMonthlyInvoices() {
      const invoices = this.billingData.invoices || [];
      const brokerCnpj = this.getBrokerCnpj();
      const monthlyGroups = {};

      invoices.forEach(inv => {
        const m = inv.month;
        if (!monthlyGroups[m]) {
          monthlyGroups[m] = {
            month: m,
            brokerRevenue: 0.0,
            partnerRevenue: 0.0,
            entitiesMap: {},
          };
        }

        const breakdownRows = inv.breakdown || [];
        breakdownRows.forEach(row => {
          const cleanPartnerCnpj = (inv.partner_cnpj || "").replace(/\D/g, "");
          const cleanBrokerCnpj = (brokerCnpj || "").replace(/\D/g, "");
          const isDirect = !cleanPartnerCnpj || cleanPartnerCnpj === cleanBrokerCnpj;
          const entityCnpj = isDirect ? brokerCnpj : inv.partner_cnpj;
          const entityName = isDirect ? this.$t('billing.table.directOperations') : inv.partner_name;

          if (!monthlyGroups[m].entitiesMap[entityCnpj]) {
            monthlyGroups[m].entitiesMap[entityCnpj] = {
              cnpj: entityCnpj,
              name: entityName,
              brokerRevenue: 0.0,
              partnerRevenue: 0.0,
              breakdown: [],
            };
          }

          if (row.revenue_total != null) {
            const bRev = row.revenue_broker || 0.0;
            const pRev = row.revenue_partner || 0.0;
            monthlyGroups[m].brokerRevenue += bRev;
            monthlyGroups[m].partnerRevenue += pRev;
            monthlyGroups[m].entitiesMap[entityCnpj].brokerRevenue += bRev;
            monthlyGroups[m].entitiesMap[entityCnpj].partnerRevenue += pRev;
          } else {
            const bRev = row.revenue || 0.0;
            monthlyGroups[m].brokerRevenue += bRev;
            monthlyGroups[m].entitiesMap[entityCnpj].brokerRevenue += bRev;
          }
          
          monthlyGroups[m].entitiesMap[entityCnpj].breakdown.push(row);
        });
      });

      return Object.values(monthlyGroups).map(group => {
        return {
          month: group.month,
          brokerRevenue: group.brokerRevenue,
          partnerRevenue: group.partnerRevenue,
          entities: Object.values(group.entitiesMap).sort((a, b) => {
            if (a.cnpj === brokerCnpj) return -1;
            if (b.cnpj === brokerCnpj) return 1;
            return a.name.localeCompare(b.name);
          }),
        };
      }).sort((a, b) => b.month.localeCompare(a.month));
    },
  },
  methods: {
    convertToCNPJ,
    ...mapActions(useBillingStore, [
      "fetchBillingStats",
      "fetchBillingStatsAllPartners",
      "fetchBillingStatsForPartner",
      "updateInvoicePaid",
    ]),

    getBrokerCnpj() {
      const raw = localStorage.getItem("brokerCnpj");
      if (!raw) return null;
      return raw.replace(/"/g, "");
    },

    async loadBillingData() {
      const brokerCnpj = this.getBrokerCnpj();
      if (!brokerCnpj) {
        console.error("No brokerCnpj found in localStorage");
        return;
      }
      try {
        if (this.isBroker) {
          if (this.selectedPartnerCnpj) {
            await this.fetchBillingStatsForPartner(brokerCnpj, this.selectedPartnerCnpj);
          } else {
            await this.fetchBillingStatsAllPartners(brokerCnpj);
          }
        } else {
          await this.fetchBillingStats(brokerCnpj);
        }
      } catch (error) {
        console.error("Failed to load billing data:", error);
      }
    },

    async onPartnerSelected() {
      await this.loadBillingData();
    },

    formatMonth(monthStr) {
      if (!monthStr) return "";
      const [year, month] = monthStr.split("-");
      const date = new Date(year, parseInt(month) - 1, 1);
      return formatDateYM(date.getTime() / 1000);
    },

    onTogglePaid(item) {
      this.pendingItem = item;
      this.confirmDialog = true;
    },

    async confirmTogglePaid() {
      if (!this.pendingItem) return;
      this.confirmingPaid = true;
      try {
        const newPaid = this.pendingItem.paid === 1 ? 0 : 1;
        await this.updateInvoicePaid(this.pendingItem.id, newPaid);
        await this.loadBillingData();
        this.confirmDialog = false;
        this.pendingItem = null;
      } catch (error) {
        console.error("Failed to toggle invoice paid status:", error);
      } finally {
        this.confirmingPaid = false;
      }
    },

     formatAmount(amount) {
       const n = Number(amount);
       if (amount === undefined || amount === null || !Number.isFinite(n)) return "0,00";
       return formatBRLPretty(n);
     },

     getRevenueValue(revObj, key) {
       if (!revObj) return 0;
       if (typeof revObj === 'object') {
         return revObj[key] !== undefined ? revObj[key] : (revObj.total || 0);
       }
       return revObj;
     },

    truncateName(name, maxLen) {
      if (!name) return "";
      return name.length > maxLen ? name.slice(0, maxLen) + "…" : name;
    },

    filterPartner(item, queryText) {
      const query = (queryText || "").toLowerCase();
      const name = (item.name || "").toLowerCase();
      const cnpj = (item.cnpj || "").replace(/\D/g, "");
      const cleanQuery = query.replace(/\D/g, "");
      return name.includes(query) || cnpj.includes(cleanQuery) || cnpj.includes(query);
    },

    getLastMonthStatusColor() {
      if (this.billingData.lastMonthPaid) return "success";
      const lastMonthInv = this.lastMonthInvoice;
      if (lastMonthInv) {
        return lastMonthInv.status === "Due" ? "warning" : "error";
      }
      return "warning";
    },

    getLastMonthStatusText() {
      if (this.billingData.lastMonthPaid) return this.$t('billing.status.paid');
      const lastMonthInv = this.lastMonthInvoice;
      if (lastMonthInv) {
        if (lastMonthInv.status === "Paid") return this.$t('billing.status.paid');
        if (lastMonthInv.status === "Due") return this.$t('billing.status.due');
        return this.$t('billing.status.overdue');
      }
      return this.$t('billing.status.due');
    },
  },
  mounted() {
    this.loadBillingData();
  },
};
</script>

<style scoped>
.billing-card {
  border-radius: 12px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: rgba(255, 255, 255, 0.03) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
}

.billing-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 20px rgba(0, 0, 0, 0.2) !important;
  border-color: rgba(255, 255, 255, 0.2) !important;
}

.billing-table-card {
  border-radius: 16px !important;
  background: rgba(255, 255, 255, 0.02) !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
}

.month-range-badge {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.9px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.45);
  background: rgba(255, 255, 255, 0.055);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 3px 10px;
  border-radius: 20px;
  white-space: nowrap;
  user-select: none;
}

.tracking-wide {
  letter-spacing: 1.5px !important;
}

.partner-selector {
  border-radius: 10px !important;
}

.partner-name {
  max-width: 320px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.partner-selection-text {
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
  vertical-align: bottom;
}

.confirm-dialog-card {
  border-radius: 14px !important;
  background: #1e1e1e !important;
}

.confirm-detail-row {
  display: flex;
  align-items: center;
  font-size: 0.9rem;
}
</style>
